import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { ClipboardList, User, Lock, Unlock, Eye, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminMedicalHistory = () => {
  const [histories, setHistories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const getToken = () => localStorage.getItem('adminToken');

  useEffect(() => { fetchHistories(); }, []);

  const fetchHistories = async () => {
    try {
      const res = await axios.get(`${API}/admin/medical-history`, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      setHistories(res.data);
    } catch (error) {
      toast.error('Failed to load medical histories');
    } finally {
      setLoading(false);
    }
  };

  const unlockHistory = async (patientId) => {
    try {
      await axios.put(`${API}/admin/medical-history/${patientId}/unlock`, {}, {
        headers: { Authorization: `Bearer ${getToken()}` }
      });
      toast.success('Medical history unlocked for patient editing');
      fetchHistories();
    } catch (error) {
      toast.error('Failed to unlock');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 lg:p-8" data-testid="admin-medical-history-page">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-[#1F2937] mb-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          Patient Medical Histories
        </h1>
        <p className="text-[#6B7280]">View and manage patient medical information</p>
      </div>

      {histories.length === 0 ? (
        <div className="bg-white rounded-lg border border-slate-200 p-12 text-center">
          <ClipboardList className="w-12 h-12 text-[#6B7280] mx-auto mb-4" />
          <p className="text-[#6B7280] text-lg">No medical histories submitted yet</p>
        </div>
      ) : (
        <div className="space-y-4">
          {histories.map(h => {
            const isExpanded = expandedId === h.patient_id;
            return (
              <div key={h.patient_id} className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden" data-testid={`mh-card-${h.patient_id}`}>
                {/* Header */}
                <div className="flex items-center justify-between p-5 cursor-pointer hover:bg-slate-50" onClick={() => setExpandedId(isExpanded ? null : h.patient_id)}>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#0F4C5C]/10 rounded-full flex items-center justify-center">
                      <User className="w-5 h-5 text-[#0F4C5C]" />
                    </div>
                    <div>
                      <div className="font-semibold text-[#1F2937]">{h.patient_name}</div>
                      <div className="text-sm text-[#6B7280]">{h.patient_email}</div>
                    </div>
                    {h.edit_requested && (
                      <span className="ml-3 px-2.5 py-1 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" /> Edit Requested
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${h.locked ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'}`}>
                      {h.locked ? 'Locked' : 'Unlocked'}
                    </span>
                    {isExpanded ? <ChevronUp className="w-5 h-5 text-[#6B7280]" /> : <ChevronDown className="w-5 h-5 text-[#6B7280]" />}
                  </div>
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-slate-200 p-5">
                    <div className="grid md:grid-cols-2 gap-6">
                      <DetailSection title="Medical Conditions">
                        {h.conditions && h.conditions.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {h.conditions.map(c => <Tag key={c} text={c} />)}
                          </div>
                        ) : <p className="text-sm text-[#6B7280]">None reported</p>}
                        {h.other_conditions && <p className="text-sm text-[#6B7280] mt-2">Other: {h.other_conditions}</p>}
                        <InfoLine label="Under physician's care" value={h.under_physician_care ? 'Yes' : 'No'} highlight={h.under_physician_care} />
                      </DetailSection>

                      <DetailSection title="Medications">
                        <InfoLine label="Taking medications" value={h.taking_medications ? 'Yes' : 'No'} highlight={h.taking_medications} />
                        {h.taking_medications && h.medications_list && <p className="text-sm text-[#1F2937] mt-1 bg-slate-50 p-2 rounded">{h.medications_list}</p>}
                      </DetailSection>

                      <DetailSection title="Allergies">
                        <InfoLine label="Has allergies" value={h.has_allergies ? 'Yes' : 'No'} highlight={h.has_allergies} />
                        {h.has_allergies && h.allergies_list && <p className="text-sm text-[#1F2937] mt-1 bg-slate-50 p-2 rounded">{h.allergies_list}</p>}
                        <InfoLine label="Anesthesia allergy" value={h.allergy_to_anesthesia ? 'Yes' : 'No'} highlight={h.allergy_to_anesthesia} />
                      </DetailSection>

                      <DetailSection title="Dental History">
                        {h.last_dental_visit && <InfoLine label="Last visit" value={new Date(h.last_dental_visit).toLocaleDateString()} />}
                        {h.previous_treatments && h.previous_treatments.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-1">
                            {h.previous_treatments.map(t => <Tag key={t} text={t} color="slate" />)}
                          </div>
                        )}
                      </DetailSection>

                      <DetailSection title="Oral Health">
                        <InfoLine label="Brushing" value={
                          h.brushing_frequency === 'twice_daily' ? 'Twice daily' :
                          h.brushing_frequency === 'once_daily' ? 'Once daily' : 'Occasionally'
                        } />
                        <InfoLine label="Flossing" value={h.floss ? 'Yes' : 'No'} />
                        <InfoLine label="Smoking" value={h.smoke ? 'Yes' : 'No'} highlight={h.smoke} />
                      </DetailSection>

                      <DetailSection title="Submission">
                        <InfoLine label="Submitted" value={new Date(h.submitted_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })} />
                        <InfoLine label="Consent" value={h.consent ? 'Given' : 'Not given'} />
                      </DetailSection>
                    </div>

                    {/* Actions */}
                    <div className="mt-6 pt-4 border-t border-slate-200 flex gap-3">
                      {h.locked && (
                        <button
                          onClick={(e) => { e.stopPropagation(); unlockHistory(h.patient_id); }}
                          className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 text-white rounded-lg hover:bg-amber-600 text-sm font-medium transition-colors"
                          data-testid={`unlock-btn-${h.patient_id}`}
                        >
                          <Unlock className="w-4 h-4" /> Unlock for Editing
                        </button>
                      )}
                      {!h.locked && (
                        <span className="flex items-center gap-1.5 px-4 py-2 bg-slate-100 text-[#6B7280] rounded-lg text-sm font-medium">
                          <Unlock className="w-4 h-4" /> Waiting for patient to update
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const DetailSection = ({ title, children }) => (
  <div>
    <h3 className="text-sm font-semibold text-[#0F4C5C] mb-2 uppercase tracking-wide">{title}</h3>
    {children}
  </div>
);

const InfoLine = ({ label, value, highlight }) => (
  <div className="flex items-center justify-between py-1 text-sm">
    <span className="text-[#6B7280]">{label}</span>
    <span className={`font-medium ${highlight ? 'text-red-600' : 'text-[#1F2937]'}`}>{value}</span>
  </div>
);

const Tag = ({ text, color = 'teal' }) => (
  <span className={`px-2 py-0.5 rounded text-xs font-medium ${
    color === 'teal' ? 'bg-[#0F4C5C]/10 text-[#0F4C5C]' : 'bg-slate-100 text-[#1F2937]'
  }`}>{text}</span>
);

export default AdminMedicalHistory;
